import React, { useCallback, useEffect, useState } from "react";
import api from "../api";
import bgVideo from "../assets/lbg.mp4";

export default function MyRentals() {
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchRentals = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const res = await api.get("/my-rentals");
      setRentals(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setError("Unable to load your rentals.");
      setRentals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRentals();
  }, [fetchRentals]);

  const formatRent = (amount) =>
    `PHP ${Number(amount || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "-");

  const leaseHealthLabel = (value) => {
    if (value === "expired") return "Expired";
    if (value === "expiring_7") return "Expiring 7d";
    if (value === "expiring_15") return "Expiring 15d";
    if (value === "expiring_30") return "Expiring 30d";
    if (value === "no_end_date") return "No End Date";
    return "Active";
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 font-sans">
      <video data-workspace-background="true" aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover"
        src={bgVideo}
        autoPlay
        muted
        loop
        playsInline
      />
      <div className="absolute inset-0 bg-slate-950/86"></div>
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/95 to-teal-950/80"></div>

      <div className="workspace-page-body relative z-10 min-h-screen px-4 py-16 text-white sm:px-6 md:py-10 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 border-b border-white/10 pb-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-teal-300">
              Tenant Rentals
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-wide md:text-4xl">
              My Rentals
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-300 md:text-base">
              View the rental properties currently assigned to your tenant
              account.
            </p>
          </div>

        </div>

        {loading ? (
          <div className="rounded-lg border border-white/10 bg-slate-950/90 py-10 text-center text-slate-300">
            Loading rentals...
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-300/30 bg-red-500/15 py-10 text-center text-red-100">
            {error}
          </div>
        ) : rentals.length === 0 ? (
          <div className="rounded-lg border border-dashed border-white/15 bg-slate-950/70 px-4 py-10 text-center text-slate-300">
            No rental property is assigned to your account yet.
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {rentals.map((rental) => (
              <div
                key={rental.id}
                className="rounded-lg border border-white/10 bg-slate-950/90 p-5 shadow-2xl"
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-white">
                      {rental.property_name}
                    </h2>
                    <p className="mt-2 text-sm text-slate-300">
                      {rental.location}
                    </p>
                  </div>
                  <span className="w-fit rounded-full border border-teal-300/40 bg-teal-500/15 px-3 py-1 text-xs font-semibold text-teal-200">
                    Assigned
                  </span>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">
                      Monthly Rent
                    </p>
                    <p className="mt-1 font-semibold text-teal-200">
                      {formatRent(rental.rent_price)}
                    </p>
                  </div>
                  <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">
                      Occupancy
                    </p>
                    <p className="mt-1 font-semibold text-white">
                      {rental.is_occupied ? "Occupied" : "Vacant"}
                    </p>
                  </div>
                </div>

                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">
                      Lease Window
                    </p>
                    <p className="mt-1 font-semibold text-slate-100">
                      {formatDate(rental.lease_start_date)} - {formatDate(rental.lease_end_date)}
                    </p>
                  </div>
                  <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">
                      Lease Health
                    </p>
                    <p className="mt-1 font-semibold text-amber-200">
                      {leaseHealthLabel(rental.lease_health)}
                    </p>
                    <p className="mt-1 text-xs text-slate-400">
                      {rental.lease_days_remaining === null || rental.lease_days_remaining === undefined
                        ? "-"
                        : `${rental.lease_days_remaining} days`}
                    </p>
                  </div>
                </div>

                {rental.description && (
                  <p className="mt-4 rounded-lg bg-white/5 p-4 text-sm text-slate-300">
                    {rental.description}
                  </p>
                )}

                {rental.lease_notes && (
                  <p className="mt-3 rounded-lg border border-white/10 bg-white/5 p-4 text-sm text-slate-300">
                    {rental.lease_notes}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
